/**
 * A job the person pasted into the free tool, for Tailor: what the tailor
 * needs of it, and the resume text at the moment they pressed.
 */
export interface PastedJob {
    title: string;
    company: string;
    /** The job's link, as `jobAddress` made it: https only. */
    address: string;
    /** The job's text, as pasted. */
    description: string;
    /** The resume box's text. Kept for the way back from sign-in, never sent to the tailor. */
    resume: string;
    /** The catalogue posting this is, on a posting's check page. */
    posting?: PostingRef;
}

/** A catalogue posting as envoy's tailor takes one: `FormRef` in `envoy_api::assist`. */
export interface PostingRef {
    /** The catalogue's `source`, as a form page's path spells it. */
    vendor: string;
    board: string;
    /** The vendor's own id for the opening: the catalogue's `external_id`. */
    posting_id: string;
}

/**
 * Tailor on a posting's check page, on its way over the sign-in: which
 * posting, and the resume box's text. The rest is the catalogue's, and is
 * read again on the way back.
 */
export interface PostingJob {
    postingId: string;
    resume: string;
}

/** What a press of Tailor carries over the sign-in. */
export type ToolJob = PastedJob | PostingJob;

export function isPostingJob(job: ToolJob): job is PostingJob {
    return "postingId" in job;
}

/** The longest address envoy takes, in characters: `MAX_ADDRESS_CHARS` in `models/page.rs`. */
export const MAX_ADDRESS_CHARS = 2_000;

const NEEDS_HTTPS = "Use a link that starts with https.";
const NOT_A_LINK = "That is not a link we can read. Paste the posting's address.";
const TOO_LONG = "That link is too long. Paste the posting's own address.";

/** A scheme that carries no host: refused as it is, never given an https in front. */
const OPAQUE_SCHEME = /^(javascript|data|vbscript|file|mailto|blob|about|tel|sms):/i;
const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:\/\//i;

/**
 * The job's link as envoy takes it: `https` only, no user or password, and
 * a host with a dot. A link with no scheme gets `https://`. Follows
 * `page_address` in `apps/envoy/src/models/page.rs`, with the dot rule so
 * a typo is caught here and not by the tailor.
 */
export function jobAddress(
    raw: string,
): { address: string; problem: null } | { address: null; problem: string } {
    const typed = raw.trim();
    if (typed === "" || OPAQUE_SCHEME.test(typed)) return { address: null, problem: NOT_A_LINK };
    const scheme = HAS_SCHEME.test(typed);
    if (scheme && !/^https:\/\//i.test(typed)) return { address: null, problem: NEEDS_HTTPS };
    const full = scheme ? typed : `https://${typed}`;
    if (Array.from(full).length > MAX_ADDRESS_CHARS) return { address: null, problem: TOO_LONG };
    let url: URL;
    try {
        url = new URL(full);
    } catch {
        return { address: null, problem: NOT_A_LINK };
    }
    if (url.protocol !== "https:" || url.username !== "" || url.password !== "") {
        return { address: null, problem: NOT_A_LINK };
    }
    if (!url.hostname.includes(".")) return { address: null, problem: NOT_A_LINK };
    if (Array.from(url.href).length > MAX_ADDRESS_CHARS) {
        return { address: null, problem: TOO_LONG };
    }
    return { address: url.href, problem: null };
}
